import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useRef, useState } from "react";
import Slider from "react-slick";
import Image from "web/react/components/product-gallery/product-gallery-image/product-gallery-image";
import MoreProducts from "web/react/components/product-gallery/product-gallery-more-products/product-gallery-more-products";
import analytics from "web/script/analytics/analytics";
import { ProductCardPropType } from "web/types/product-card";
import { useLeadHref } from "web/react/components/lead-link/lead-link";
import "../slick-theme.less";
import "../slick.less";
import styles from "./product-gallery-carousel.css";

let firstClientX;

function ProductGalleryCarousel({
    analyticsAction,
    dotsInsideGallery,
    firstInteraction,
    handleFirstInteraction,
    images,
    isFullscreen,
    relatedProducts,
    handleImageClick,
    settings,
    showRelatedProducts,
    currentLinkId,
    swipeRedirect,
}) {
    let lastImageIndex = images.length - 1;
    lastImageIndex = showRelatedProducts ? lastImageIndex + 1 : lastImageIndex;

    const [seenAll, setSeenAll] = useState(false);
    const [relatedSeen, setRelatedSeen] = useState(false);
    const [imagesSeen, setImagesSeen] = useState(0);
    const [interactionCount, setInteractionCount] = useState(0);
    const leadUrl = useLeadHref("/track/lead/" + currentLinkId, "redirect_on_swipe");

    const sliderRef = useRef(null);

    let galleryElements = [];

    galleryElements = images.map((image, index) => {
        return (
            /* hack to get slick-slider ssr to set slick-track width to NaN (ignored by the
             * browser) instead of 0, which causes the slides to start in the wrong place until
             * the client version is initialized*/
            <div key={image.large_url} style={{}}>
                <Image
                    isFullscreen={isFullscreen}
                    onClick={() => onImageClick(index)}
                    image={image}
                />
            </div>
        );
    });

    if (showRelatedProducts) {
        galleryElements.push(
            <MoreProducts
                key="related-slide"
                products={relatedProducts}
                fullscreen={isFullscreen}
            />
        );
    }

    const handleTouchStart = useCallback((event) => {
        if (event && event.touches.length > 0) {
            firstClientX = event.touches[0].clientX;
        }
    }, []);

    let handleTouchMoved = useCallback((event) => {
        if (event.touches.length > 0) {
            const minValue = 20;
            const clientX = event.touches[0].clientX - firstClientX;

            // Disable vertical scrolling when user swipes horizontally (Safari fix).
            if (Math.abs(clientX) > minValue) {
                event.preventDefault();
                event.returnValue = false;
                return false;
            }
        }
    }, []);

    const sliderElement = useCallback(
        (node) => {
            if (node === null) {
                return;
            }
            node.addEventListener("touchmove", handleTouchMoved, {
                passive: false,
            });
            node.addEventListener("gesturestart", (event) => event.preventDefault());
        },
        [handleTouchMoved]
    );

    function getDotsStyle(dots, dotsInsideGallery) {
        if (dotsInsideGallery) {
            return (
                <div>
                    <div className={styles.dotsContainer}>
                        <ul className={styles.dots}> {dots} </ul>
                    </div>
                </div>
            );
        }
        return <ul> {dots} </ul>;
    }

    function onImageClick(index) {
        analytics.event("engagement", analyticsAction, "open_fullscreen");
        if (isFullscreen) {
            return;
        }
        handleImageClick(index);
    }

    function onSlideChange(currentSlide, nextSlide) {
        analytics.event("engagement", analyticsAction, "changed_image");

        if (swipeRedirect && interactionCount != null) {
            setInteractionCount(interactionCount + 1);
        }

        if (!firstInteraction) {
            handleFirstInteraction();
        }

        if (!seenAll) {
            let tempImagesSeen = imagesSeen;
            if (nextSlide === currentSlide + 1) {
                // User scrolls right
                tempImagesSeen += 1;
            } else if (nextSlide === currentSlide - 1 || nextSlide === lastImageIndex) {
                // User scrolls left
                tempImagesSeen -= 1;
            }

            if (Math.abs(tempImagesSeen) === lastImageIndex) {
                setSeenAll(true);
                analytics.event("engagement", analyticsAction, "seen_all");
            } else {
                setImagesSeen(tempImagesSeen);
            }
        }

        if (showRelatedProducts && !relatedSeen && nextSlide === lastImageIndex) {
            setRelatedSeen(true);
            analytics.event("engagement", analyticsAction, "seen_related_products_slide");
        }

        // pmt_221 - after swiping twice we load the partner page, this is a onetime action
        if (interactionCount >= 1) {
            setInteractionCount(null);
            analytics.event("engagement", "redirect_on_swipe");
            window.open(leadUrl, "_blank");
        }
    }

    function handleOnSwipe() {
        if (sliderRef) {
            sliderRef.current.innerSlider.clickable = true;
        }
    }

    const sliderClasses = classnames(
        { [styles.carouselImage]: !isFullscreen },
        // eslint-disable-next-line css-modules/no-undef-class
        { [styles.dotsInside]: dotsInsideGallery },
        // eslint-disable-next-line css-modules/no-undef-class
        { [styles.fullscreen]: isFullscreen }
    );

    return (
        <div
            ref={sliderElement}
            onTouchStart={handleTouchStart}
            className={isFullscreen ? "" : styles.carousel}
        >
            <Slider
                ref={sliderRef}
                className={sliderClasses}
                beforeChange={onSlideChange}
                appendDots={(dots) => getDotsStyle(dots, dotsInsideGallery)}
                {...settings}
                // Workaround for click bug
                // https://github.com/akiran/react-slick/issues/604#issuecomment-429166338
                onSwipe={handleOnSwipe}
            >
                {galleryElements}
            </Slider>
        </div>
    );
}

export default ProductGalleryCarousel;

ProductGalleryCarousel.propTypes = {
    analyticsAction: PropTypes.string.isRequired,
    dotsInsideGallery: PropTypes.bool,
    firstInteraction: PropTypes.bool,
    handleFirstInteraction: PropTypes.func,
    images: PropTypes.array.isRequired,
    isFullscreen: PropTypes.bool,
    relatedProducts: PropTypes.arrayOf(ProductCardPropType),
    setCurrentSlideIndex: PropTypes.func,
    handleImageClick: PropTypes.func,
    settings: PropTypes.object,
    showRelatedProducts: PropTypes.bool,
    imageType: PropTypes.string,
    currentLinkId: PropTypes.string,
    swipeRedirect: PropTypes.bool,
};

ProductGalleryCarousel.defaultProps = {
    dotsInsideGallery: true,
    firstInteraction: true,
    isFullscreen: false,
    showRelatedProducts: false,
};
